// @noflow
// $FlowFixMe - external module
import { PublicClientApplication, LogLevel } from '@azure/msal-browser';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Azure AD / Microsoft Entra ID Authentication Configuration
 *
 * Fortress-Owned Multi-Tenant SSO:
 * - ONE Fortress Azure AD app registration supports all customers
 * - Uses 'common' authority (accepts users from any Azure AD tenant)
 * - Backend validates tenant against AZURE_ALLOWED_TENANTS allowlist
 *
 * Environment Variables:
 * - REACT_APP_AZURE_CLIENT_ID: (Required) Fortress's Azure AD application (client) ID
 *
 * Customer Onboarding:
 * 1. Get customer's Azure AD tenant ID
 * 2. Add tenant ID -> organization mapping in Fortress database (SsoProviderOrganization)
 * 3. Add customer's tenant ID to OneElmington's Fortress Azure AD app registration allowlist
 * 4. Add customer's vanity URL to LaunchDarkly targeting rules for the "azure-sso-enabled-domains" flag
 * 5. Customer admin grants consent (first login or pre-consent link)
 * 6. Done! No customer Azure AD app configuration needed.
 */

export const msalConfig: any = {
  auth: {
    clientId: import.meta.env.REACT_APP_AZURE_CLIENT_ID || '',
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: 'sessionStorage', // Use sessionStorage instead of localStorage for security
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: number,
        message: string,
        containsPii: boolean,
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
      logLevel:
        import.meta.env.MODE === 'development'
          ? LogLevel.Verbose
          : LogLevel.Error,
    },
  },
};

/**
 * Scopes requested during login
 *
 * Required scopes:
 * - openid: Required for identity
 * - profile: Gets user's name
 * - email: Gets user's email address
 */
export const loginRequest: { scopes: Array<string> } = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * MSAL instance - singleton
 * This handles all Azure AD authentication operations
 */
let msalInstance: ?PublicClientApplication = null;

export const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};

/**
 * Initialize MSAL
 * Call this once during app startup (before render)
 */
export const initializeMsal = async (): Promise<void> => {
  const instance = getMsalInstance();
  await instance.initialize();
};

/**
 * Check if Azure AD is configured
 * Only requires CLIENT_ID - tenant ID defaults to 'common' for multi-tenant
 */
export const isAzureAuthConfigured = (): boolean => {
  return Boolean(import.meta.env.REACT_APP_AZURE_CLIENT_ID);
};

/**
 * Check if Azure SSO should be shown for the current domain
 *
 * React Hook - Uses LaunchDarkly flag: azure-sso-enabled-domains
 *
 * LaunchDarkly Configuration:
 * - Flag name: azure-sso-enabled-domains
 * - Configure targeting rules in LaunchDarkly based on the 'hostname' attribute
 * - The hostname attribute is automatically passed from the LaunchDarkly context
 *
 * Returns:
 * - false if Azure is not configured
 * - flag value (true/false) based on LaunchDarkly targeting rules for the current hostname
 */
export const useShouldShowAzureSSO = (): boolean => {
  const { azureSsoEnabledDomains } = useFlags();

  // First check if Azure is configured at all
  if (!isAzureAuthConfigured()) {
    return false;
  }
  return Boolean(azureSsoEnabledDomains);
};

/**
 * Clear MSAL cache and accounts
 * Call this on logout to ensure clean state for next login
 */
export const clearMsalCache = (): void => {
  if (!msalInstance) {
    return;
  }

  try {
    // MSAL v2.x manages accounts automatically in sessionStorage
    // No need to call removeAccount - just clear storage

    // Clear browser storage cache
    sessionStorage.removeItem('msal.interaction.status');
    sessionStorage.removeItem('msal.token.keys');
    sessionStorage.removeItem('msal.account.keys');

    // Clear any other MSAL-related keys
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('msal.')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing MSAL cache:', error);
  }
};
